import { CarvanaHeaderProvider } from '@carvana/header';
import dynamic from 'next/dynamic';
import React from 'react';
import { PortalContextProvider } from '@/common/Portal/PortalContext';
import HomeData from '@/home/HomeData';
import COOKIES from '~/constants/cookies';
import {
  browserCookieIdExperiments,
  userIdExperiments
} from '~/constants/home/experiments';
import AnalyticsProvider from '~/context/common/AnalyticsProvider';
import DeviceContext, {
  DeviceContextProvider,
  type OwnProps as DeviceContextProps
} from '~/context/common/DeviceContext';
import ExperimentProvider, {
  type PropsFromServer as ExperimentContextProps
} from '~/context/common/ExperimentProvider';
import {
  StaticContextProvider,
  type OwnProps as StaticContextProps
} from '~/context/common/StaticContext';
import {
  UserContextProvider,
  type OwnProps as UserContextProps
} from '~/context/common/UserContext';
import { AvailabilityContextProvider } from '~/context/home/AvailabilityContext';
import { SavedSearchesContextProvider } from '~/context/home/SavedSearchesContext';
import { BrowserCookies } from '~/utilities';
import {
  AcquisitionContextProvider,
  type OwnProps as AcquisitionContextProps
} from './AcquisitionContext';
import { FinanceContextProvider } from './FinanceContext';
import {
  InternalLinkContextProvider,
  type OwnProps as InternalLinkContextProps
} from './InternalLinkContext';
import { ModalContextProvider } from './ModalContext';
import { NextBestActionContextProvider } from './NextBestActionContext';
import RecentSearchesContext, {
  RecentSearchesContextProvider,
  type OwnProps as RecentSearchesContextType
} from './RecentSearchesContext';
import RecentVehiclesContext, {
  RecentVehiclesContextProvider,
  type OwnProps as RecentVehiclesContextType
} from './RecentVehiclesContext';
import { SuggestedForYouContextProvider } from './SuggestedForYouContext';
import { ErrorContextProvider } from '../common/ErrorContext';
import { TradeContextProvider } from '../common/TradeContext';

const CoreAuth = dynamic(
  () => import(/* webpackChunkName: "CoreAuth" */ '@/common/CoreAuth'),
  { ssr: false }
);

export type HomeProvidersProps = {
  forAcquisitionContext: AcquisitionContextProps;
  forDeviceContext: DeviceContextProps;
  forExperimentContext: ExperimentContextProps;
  forInternalLinkContext: InternalLinkContextProps;
  forRecentSearchesContext: RecentSearchesContextType;
  forRecentVehiclesContext: RecentVehiclesContextType;
  forStaticContext: StaticContextProps;
  forUserContext: UserContextProps;
};

type OwnProps = React.PropsWithChildren<
  HomeProvidersProps & {
    eventableProject: string;
    eventableTrack: string;
  }
>;

export default function HomeProviders({
  children,
  eventableProject,
  eventableTrack,
  forAcquisitionContext,
  forDeviceContext,
  forExperimentContext,
  forInternalLinkContext,
  forRecentSearchesContext,
  forRecentVehiclesContext,
  forStaticContext,
  forUserContext
}: OwnProps) {
  return (
    <StaticContextProvider {...forStaticContext}>
      <ExperimentProvider
        {...forExperimentContext}
        browserCookieIdExperiments={browserCookieIdExperiments}
        userIdExperiments={userIdExperiments}
      >
        <CarvanaHeaderProvider>
          <ErrorContextProvider>
            <DeviceContextProvider {...forDeviceContext}>
              <AcquisitionContextProvider {...forAcquisitionContext}>
                <InternalLinkContextProvider {...forInternalLinkContext}>
                  <UserContextProvider {...forUserContext}>
                    <RecentSearchesContextProvider
                      {...forRecentSearchesContext}
                    >
                      <FinanceContextProvider>
                        <TradeContextProvider>
                          <NextBestActionContextProvider>
                            <SavedSearchesContextProvider>
                              <AvailabilityContextProvider>
                                <RecentVehiclesContextProvider
                                  {...forRecentVehiclesContext}
                                >
                                  <SuggestedForYouContextProvider>
                                    <DeviceContext.Consumer>
                                      {({ nativeDeviceType }) => (
                                        <RecentSearchesContext.Consumer>
                                          {({ recentSearches }) => (
                                            <RecentVehiclesContext.Consumer>
                                              {({ recentVehiclesFromBff }) => (
                                                <AnalyticsProvider
                                                  projectName={eventableProject}
                                                  payloadMetaAttributes={{
                                                    isReturningUser:
                                                      recentVehiclesFromBff.data
                                                        .length +
                                                        recentSearches.data
                                                          .length >
                                                      0,
                                                    isWebview:
                                                      nativeDeviceType.isNative,
                                                    webview:
                                                      nativeDeviceType.isNative
                                                        ? {
                                                            platform:
                                                              nativeDeviceType.platform,
                                                            version:
                                                              BrowserCookies.get(
                                                                COOKIES.nativeAppVersion
                                                              )
                                                          }
                                                        : null
                                                  }}
                                                  track={eventableTrack}
                                                >
                                                  <ModalContextProvider>
                                                    <PortalContextProvider>
                                                      <CoreAuth />
                                                      <HomeData />
                                                      {children}
                                                    </PortalContextProvider>
                                                  </ModalContextProvider>
                                                </AnalyticsProvider>
                                              )}
                                            </RecentVehiclesContext.Consumer>
                                          )}
                                        </RecentSearchesContext.Consumer>
                                      )}
                                    </DeviceContext.Consumer>
                                  </SuggestedForYouContextProvider>
                                </RecentVehiclesContextProvider>
                              </AvailabilityContextProvider>
                            </SavedSearchesContextProvider>
                          </NextBestActionContextProvider>
                        </TradeContextProvider>
                      </FinanceContextProvider>
                    </RecentSearchesContextProvider>
                  </UserContextProvider>
                </InternalLinkContextProvider>
              </AcquisitionContextProvider>
            </DeviceContextProvider>
          </ErrorContextProvider>
        </CarvanaHeaderProvider>
      </ExperimentProvider>
    </StaticContextProvider>
  );
}
